<script lang="ts" setup>
import { SancareDateRangePicker, SancarePagination } from '@sancare/typescript-commons'
import dayjs from '@sancare/typescript-commons/src/misc/dayjs'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LogEvent } from '@/maintenance/types'
import { useStore } from '@/store'
import { ConnectorFlowType } from '@/store/modules/connector-config/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'

const store = useStore()
const { t } = useI18n()
const router = useRouter()

const props = defineProps<{
  pmsiType: PmsiTypeEnum
}>()

const getLogEventsRequest = computed(() => store.state.maintenance.requests.getLogEventsRequest)
const pageCount = computed<number>(() => store.state.maintenance.logs.pageCount)
const pageSize = ref(50)
const logEvents = computed<LogEvent[]>(() => store.state.maintenance.logs.logEvents)
const logEventsHeaders = computed<string[]>(() => {
  if (!store.state.maintenance.logs.logEvents.length) {
    return []
  }
  return Object.keys(store.state.maintenance.logs.logEvents[0]).filter((key) => key !== 'id')
})
const currentPage = ref(1)
const earlyLimit = ref(dayjs().startOf('week'))
const lateLimit = ref(dayjs().endOf('week'))
const pmsiTypeSelect = ref(props.pmsiType)
const trigger = ref('')
const messageContains = ref('')
const responseContains = ref('')

watch(() => pmsiTypeSelect.value, () => {
  router.replace({ name: 'maintenance-event-observer', params: { pmsiType: pmsiTypeSelect.value } })
})

function setPage(page: number) {
  currentPage.value = page
  doRequest()
}

function doRequest() {
  store.dispatch(
    'getLogEvents',
    {
      pmsiType: props.pmsiType,
      earlyLimit: earlyLimit.value.format('YYYY-MM-DD'),
      lateLimit: lateLimit.value.format('YYYY-MM-DD'),
      messageContains: messageContains.value,
      responseContains: responseContains.value,
      trigger: trigger.value,
      page: currentPage.value,
      pageSize: pageSize.value,
    }
  )
}

function showLogEvent(logEvent: LogEvent) {
  store.commit('setShownLogEventId', logEvent.id)
  router.push({ name: 'maintenance-event-visualizer', params: { pmsiType: props.pmsiType } })
}

onMounted(() => {
  nextTick(() => {
    const hash = router.currentRoute.value.hash
    if (!router.currentRoute.value.hash) {
      return
    }
    const anchor = hash.replace('#', '')
    const elem = document.getElementById(anchor)
    if (!elem) {
      return
    }
    elem.scrollIntoView()
  })
  if (!getLogEventsRequest.value.ok) {
    doRequest()
  }
})
</script>
<template>
  <div class="w-100 p-5">
    <div class="card">
      <div class="card-body">
        <h1 class="mb-4">
          {{ t('maintenance.eventObserver.title') }}
        </h1>
        <div class="row align-items-end justify-content-center g-3">
          <div class="col-auto">
            <select
              id="form-pmsi-type"
              v-model="pmsiTypeSelect"
              name="form-pmsi-type"
              class="form-select"
            >
              <option
                :value="PmsiTypeEnum.MCO"
              >
                {{ PmsiTypeEnum.MCO.toUpperCase() }}
              </option>
              <option
                :value="PmsiTypeEnum.SMR"
              >
                SMR
              </option>
            </select>
          </div>
          <div class="col-auto">
            <sancare-date-range-picker
              v-model:start-date="earlyLimit"
              v-model:end-date="lateLimit"
              class="d-inline-block"
            />
          </div>
          <div class="col-auto">
            <label
              class="fw-bold"
              for="form-message-contains"
            >{{ t('maintenance.eventObserver.messageContains') }}</label>
            <input
              id="form-message-contains"
              v-model="messageContains"
              type="text"
              class="form-control"
              name="form-message-contains"
              @keyup.enter="doRequest"
            >
          </div>
          <div class="col-auto">
            <label
              class="fw-bold"
              for="form-response-contains"
            >{{ t('maintenance.eventObserver.responseContains') }}</label>
            <input
              id="form-response-contains"
              v-model="responseContains"
              type="text"
              class="form-control"
              name="form-response-contains"
              @keyup.enter="doRequest"
            >
          </div>
          <div class="col-auto">
            <label
              class="fw-bold"
              for="form-trigger"
            >{{ t('connectorConfig.trigger') }}</label>
            <input
              id="form-trigger"
              v-model="trigger"
              type="text"
              class="form-control"
              name="form-trigger"
              @keyup.enter="doRequest"
            >
          </div>
          <div class="col-auto align-self-end">
            <button
              class="btn btn-primary"
              :class="{disabled: getLogEventsRequest.fetching}"
              @click="doRequest"
            >
              {{ t('maintenance.eventObserver.filterButton') }}
            </button>
          </div>
        </div>

        <div class="d-flex flex-row flex-wrap justify-content-start align-items-baseline align-content-stretch">
          <div
            v-if="logEvents.length"
            class="d-flex flex-wrap flex-row align-content-stretch justify-content-center w-100 mt-3"
          >
            <sancare-pagination
              v-if="pageCount > 1"
              :page-count="pageCount"
              :current-page="currentPage"
              @set-page="setPage"
            />
            <table class="table table-hover">
              <thead>
                <tr>
                  <th
                    v-for="logEventsHeader in logEventsHeaders"
                    :key="logEventsHeader"
                  >
                    {{ logEventsHeader }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="logEvent in logEvents"
                  :id="'event-'+logEvent.id"
                  :key="logEvent.id"
                >
                  <td
                    v-for="logEventsHeader in logEventsHeaders"
                    :key="logEventsHeader"
                  >
                    <div
                      :style="['response', 'message'].includes(logEventsHeader) ? { wordBreak:'break-all', maxHeight:'100px', overflow:'scroll' } : {}"
                    >
                      {{ logEvent[logEventsHeader] }}
                    </div>
                  </td>
                  <td>
                    <button
                      class="btn btn-link"
                      @click="showLogEvent(logEvent)"
                    >
                      Voir &rarr;
                    </button>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <th
                    v-for="logEventsHeader in logEventsHeaders"
                    :key="logEventsHeader"
                  >
                    {{ logEventsHeader }}
                  </th>
                </tr>
              </tfoot>
            </table>
            <sancare-pagination
              v-if="pageCount > 1"
              :page-count="pageCount"
              :current-page="currentPage"
              @set-page="setPage"
            />
          </div>
          <div
            v-else-if="getLogEventsRequest.fetching"
            class="loader loader-lg loader-center m-auto mt-3"
          />
          <div
            v-else-if="logEvents.length === 0 && getLogEventsRequest.ok"
            class="m-auto mt-3"
          >
            <span>{{ t('maintenance.eventObserver.noResults') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
h1 {
  font-size: 1.5rem;
}
</style>